import * as Yup from 'yup';

import Enums from '@core/types/api/Enums';
import type { Role } from '@core/types/api/Role/Role';
import type { User } from '@core/types/api/User/User';
import type { PermissionData } from '@core/utils/permissions';
import { NIL_UUID } from '@core/utils/uuid';
import {
  YupBoolean,
  YupEmail,
  YupNullableString,
  YupNullableTel,
  YupReference,
  type YupSchemaReferenceType,
  YupString,
} from '@core/utils/yupHelpers';

export const BULK_EDIT_PERMISSIONS = [
  Enums.Permission.CanBulkEditQuotes,
  Enums.Permission.CanBulkEditProjects,
  Enums.Permission.CanBulkEditCompanies,
  Enums.Permission.CanBulkEditLocations,
  Enums.Permission.CanBulkEditActivities,
] as const;

export type BulkEditPermissionName = (typeof BULK_EDIT_PERMISSIONS)[number];

export const bulkEditPermissionData: Record<BulkEditPermissionName, PermissionData> = {
  [Enums.Permission.CanBulkEditQuotes]: {
    label: 'Quotes',
    subLabel: 'Includes price escalations and quote product price updates',
  },
  [Enums.Permission.CanBulkEditProjects]: {
    label: 'Projects',
  },
  [Enums.Permission.CanBulkEditCompanies]: {
    label: 'Companies',
  },
  [Enums.Permission.CanBulkEditLocations]: {
    label: 'Locations',
  },
  [Enums.Permission.CanBulkEditActivities]: {
    label: 'Activities',
  },
};

type BulkEditFieldName =
  | 'canBulkEditQuotes'
  | 'canBulkEditProjects'
  | 'canBulkEditCompanies'
  | 'canBulkEditLocations'
  | 'canBulkEditActivities';

export const BULK_EDIT_FIELD_MAP: Record<BulkEditPermissionName, BulkEditFieldName> = {
  [Enums.Permission.CanBulkEditQuotes]: 'canBulkEditQuotes',
  [Enums.Permission.CanBulkEditProjects]: 'canBulkEditProjects',
  [Enums.Permission.CanBulkEditCompanies]: 'canBulkEditCompanies',
  [Enums.Permission.CanBulkEditLocations]: 'canBulkEditLocations',
  [Enums.Permission.CanBulkEditActivities]: 'canBulkEditActivities',
};

export const PLANNING_PERMISSIONS = [
  Enums.Permission.CanViewPlanning,
  Enums.Permission.CanEditPlanning,
  Enums.Permission.CanApprovePlanning,
] as const;

export type PlanningPermissionName = (typeof PLANNING_PERMISSIONS)[number];

export const planningPermissionData: Record<PlanningPermissionName, PermissionData> = {
  [Enums.Permission.CanViewPlanning]: {
    label: 'View',
  },
  [Enums.Permission.CanEditPlanning]: {
    label: 'Edit',
  },
  [Enums.Permission.CanApprovePlanning]: {
    label: 'Approve',
  },
};

export type PlanningFieldName = 'canViewPlanning' | 'canEditPlanning' | 'canApprovePlanning';

export const PLANNING_FIELD_MAP: Record<PlanningPermissionName, PlanningFieldName> = {
  [Enums.Permission.CanViewPlanning]: 'canViewPlanning',
  [Enums.Permission.CanEditPlanning]: 'canEditPlanning',
  [Enums.Permission.CanApprovePlanning]: 'canApprovePlanning',
};

export interface UserFormikType {
  id: string;
  firstName: string;
  lastName: string | null;
  emailAddress: string;
  phoneNumber: string | null;
  canReceiveEmail: boolean;
  roles: YupSchemaReferenceType[];
  allMarkets: boolean;
  marketSummaries: YupSchemaReferenceType[];
  canBulkEdit: boolean;
  canBulkEditQuotes: boolean;
  canBulkEditProjects: boolean;
  canBulkEditCompanies: boolean;
  canBulkEditLocations: boolean;
  canBulkEditActivities: boolean;
  canViewSensitiveFiles: boolean;
  canEditAllActivities: boolean;
  canViewPlanning: boolean;
  canEditPlanning: boolean;
  canApprovePlanning: boolean;
}

export const UserSchema: Yup.AnyObjectSchema = Yup.object().shape({
  firstName: YupString('First name'),
  lastName: YupNullableString('Last name'),
  emailAddress: YupEmail('Email address'),
  phoneNumber: YupNullableTel('Phone number'),
  canReceiveEmail: YupBoolean('Can receive email'),
  roles: Yup.array().of(YupReference('Role')).required(),
  // The Markets input is only rendered while this toggle is off, so the "both at once" failure
  // is reported here instead — on a field the user can always see.
  allMarkets: YupBoolean('All markets').test(
    'no-individual-markets-when-all-markets',
    'Turn this off to assign individual markets',
    function noIndividualMarketsWhenAllMarkets(allMarkets): boolean {
      const marketSummaries = (this.parent as UserFormikType | undefined)?.marketSummaries ?? [];
      return allMarkets !== true || marketSummaries.length === 0;
    },
  ),
  marketSummaries: Yup.array()
    .of(YupReference('Markets'))
    .when('allMarkets', {
      is: true,
      then: (schema) => schema.max(0),
      otherwise: (schema) => schema.required(),
    }),
  canBulkEdit: YupBoolean('Can Bulk Edit'),
  canBulkEditQuotes: YupBoolean('Bulk edit quotes'),
  canBulkEditProjects: YupBoolean('Bulk edit projects'),
  canBulkEditCompanies: YupBoolean('Bulk edit companies'),
  canBulkEditLocations: YupBoolean('Bulk edit locations'),
  canBulkEditActivities: YupBoolean('Bulk edit activities'),
  canViewSensitiveFiles: YupBoolean('Can view sensitive files'),
  canEditAllActivities: YupBoolean('Can edit all activities'),
  // View gates the Planning page, so edit or approve without it is unusable.
  // The server rejects that set too; this keeps the error on a visible switch.
  canViewPlanning: YupBoolean('Can view planning').test(
    'planning-edit-and-approve-require-view',
    'Turn on View to grant Edit or Approve',
    function planningEditAndApproveRequireView(canViewPlanning): boolean {
      const parent = this.parent as UserFormikType | undefined;
      return canViewPlanning === true || !(parent?.canEditPlanning || parent?.canApprovePlanning);
    },
  ),
  canEditPlanning: YupBoolean('Can edit planning'),
  canApprovePlanning: YupBoolean('Can approve planning'),
});

// Planning permissions a user holds through their roles. The switches show
// these as on and locked; only direct grants are edited and saved.
export const getRoleGrantedPlanningPermissions = (
  roles: Pick<Role, 'permissions'>[],
): Set<PlanningPermissionName> =>
  new Set(
    PLANNING_PERMISSIONS.filter((permission) =>
      roles.some((role) => role.permissions.includes(permission)),
    ),
  );

// Keeps the form inside the server's rule: enabling edit or approve turns view
// on, and turning view off drops edit and approve.
export const setPlanningPermission = (
  field: PlanningFieldName,
  checked: boolean,
): Partial<Pick<UserFormikType, PlanningFieldName>> => {
  if (field === 'canViewPlanning') {
    return checked
      ? { canViewPlanning: true }
      : { canViewPlanning: false, canEditPlanning: false, canApprovePlanning: false };
  }
  return checked ? { [field]: true, canViewPlanning: true } : { [field]: false };
};

export const getEditableBulkEditPermissions = (values: UserFormikType): Enums.Permission[] => {
  if (!values.canBulkEdit) {
    return [];
  }

  return BULK_EDIT_PERMISSIONS.filter((permission) => values[BULK_EDIT_FIELD_MAP[permission]]);
};

// The submitted array replaces the user's stored user_permissions wholesale on
// the server, so every editable user-level permission must be included here or
// saving a user silently revokes it.
export const getEditableUserPermissions = (values: UserFormikType): Enums.Permission[] => {
  const permissions = [...getEditableBulkEditPermissions(values)];

  if (values.canViewSensitiveFiles) {
    permissions.push(Enums.Permission.CanViewSensitiveFiles);
  }

  if (values.canEditAllActivities) {
    permissions.push(Enums.Permission.CanEditAllActivities);
  }

  permissions.push(
    ...PLANNING_PERMISSIONS.filter((permission) => values[PLANNING_FIELD_MAP[permission]]),
  );

  return permissions;
};

export const FormikUser = (user: User | null): UserFormikType => {
  if (user === null) {
    return {
      id: NIL_UUID,
      firstName: '',
      lastName: null,
      emailAddress: '',
      phoneNumber: null,
      canReceiveEmail: false,
      roles: [],
      allMarkets: false,
      marketSummaries: [],
      canBulkEdit: true,
      canBulkEditQuotes: true,
      canBulkEditProjects: true,
      canBulkEditCompanies: true,
      canBulkEditLocations: true,
      canBulkEditActivities: true,
      canViewSensitiveFiles: false,
      canEditAllActivities: false,
      canViewPlanning: false,
      canEditPlanning: false,
      canApprovePlanning: false,
    };
  }

  const editablePermissionSet = new Set(user.permissions);
  const editablePermissionFields = Object.fromEntries(
    BULK_EDIT_PERMISSIONS.map((permission) => [
      BULK_EDIT_FIELD_MAP[permission],
      editablePermissionSet.has(permission),
    ]),
  ) as Pick<UserFormikType, BulkEditFieldName>;
  // Planning is also granted by the admin roles. Hydrating from the direct
  // grants keeps a save from persisting role-inherited access as user rows.
  const userPermissionSet = new Set(user.userPermissions);
  const planningPermissionFields = Object.fromEntries(
    PLANNING_PERMISSIONS.map((permission) => [
      PLANNING_FIELD_MAP[permission],
      userPermissionSet.has(permission),
    ]),
  ) as Pick<UserFormikType, PlanningFieldName>;

  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    emailAddress: user.emailAddress,
    phoneNumber: user.phoneNumber,
    canReceiveEmail: user.canReceiveEmail,
    roles: user.roles.map((role) => ({
      id: role.id,
      option: { value: role.id, label: role.displayName() },
    })),
    allMarkets: user.allMarkets,
    // A user associated with all markets must not carry individual markets. Legacy rows can
    // violate that, and keeping them here would fail validation against a field the form does
    // not render, blocking every save with no visible error. Dropping them lets the next save
    // repair the record.
    marketSummaries: user.allMarkets
      ? []
      : user.marketSummaries.map(({ id, name }) => ({
          id,
          option: { value: id, label: name },
        })),
    canBulkEdit: Object.values(editablePermissionFields).some(Boolean),
    ...editablePermissionFields,
    canViewSensitiveFiles: editablePermissionSet.has(Enums.Permission.CanViewSensitiveFiles),
    // Hydrated from direct grants so a save does not persist what the admin roles already give.
    canEditAllActivities: userPermissionSet.has(Enums.Permission.CanEditAllActivities),
    ...planningPermissionFields,
  };
};
